import { useState } from "react";
import { PageHeader } from "@/components/Layout";
import { ErrorNote } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { useAudit } from "@/lib/queries";
import { Pager } from "@/pages/Events";

/*
 * Audit: chi, quando, che cosa — e il valore precedente dove c'è.
 *
 * Due pubblici. ADMIN vede tutto. **Un CONTABILE vede le entità del registro**,
 * e non è una concessione: i movimenti sono immutabili e si correggono
 * stornando, e l'unico posto in cui è scritto CHI ha registrato uno storno e
 * PERCHÉ è questa tabella. Conti, proxy e utenti restano fuori.
 *
 * Il filtro lo applica il backend dentro la query: qui si dice soltanto che è
 * attivo, perché una lista ristretta senza dirlo sembra una lista vuota.
 */

const PAGE = 50;

/** Le azioni che si cercano più spesso, senza pretendere di elencarle tutte. */
const AZIONI = [
  "TRANSACTION_REVERSED",
  "PAYOUT_STATUS_CHANGED",
  "SESSION_FAILED",
  "SESSION_COMPLETED",
  "RISK_EVENT_RESOLVED",
  "LEDGER_START_ANCHORED",
  "CYCLE_CURRENCY_REALIGNED",
] as const;

export function Audit() {
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [entityId, setEntityId] = useState("");
  const [offset, setOffset] = useState(0);

  const q = useAudit({
    ...(entityType ? { entityType } : {}),
    ...(action ? { action } : {}),
    ...(entityId.trim() ? { entityId: entityId.trim() } : {}),
    limit: PAGE,
    offset,
  });

  const tipi = q.data?.scope ?? ["Transaction", "Payout", "Cycle", "HedgeLeg", "TradingAccount", "Client", "Proxy", "HedgeSession", "User", "RiskEvent"];

  const change = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setOffset(0);
  };

  return (
    <>
      <PageHeader title="Audit" subtitle="Chi ha fatto che cosa, e quando" />

      <div className="space-y-4 px-6 py-4">
        {q.data?.scope && (
          <p className="rounded border border-line bg-raised/50 px-3 py-2 text-sm text-muted">
            Stai vedendo le entità del <strong className="text-ink">registro</strong>: movimenti, payout, cicli e gambe. I movimenti non si
            modificano — si stornano — e qui è scritto chi l'ha fatto e perché. Conti, proxy e utenti sono fuori da questa vista.
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          <select value={entityType} onChange={(e) => change(setEntityType)(e.target.value)} className="rounded border border-line bg-surface px-2 py-1.5 text-sm">
            <option value="">Tutte le entità</option>
            {tipi.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select value={action} onChange={(e) => change(setAction)(e.target.value)} className="rounded border border-line bg-surface px-2 py-1.5 text-sm">
            <option value="">Tutte le azioni</option>
            {AZIONI.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <input
            value={entityId}
            onChange={(e) => change(setEntityId)(e.target.value)}
            placeholder="Id della riga: la storia di quel conto, di quel movimento…"
            className="min-w-[22rem] flex-1 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </div>

        {q.isLoading ? (
          <p className="text-sm text-muted">Caricamento…</p>
        ) : q.error ? (
          <ErrorNote>{errorMessage(q.error)}</ErrorNote>
        ) : !q.data?.rows.length ? (
          <p className="card px-4 py-4 text-sm text-muted">Nessuna riga con questi filtri.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs text-faint">
                    <th className="py-2 pr-4 font-medium">Quando</th>
                    <th className="py-2 pr-4 font-medium">Chi</th>
                    <th className="py-2 pr-4 font-medium">Azione</th>
                    <th className="py-2 pr-4 font-medium">Su che cosa</th>
                    <th className="py-2 font-medium">Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.rows.map((r) => (
                    <tr key={r.id} className="border-b border-line/60 align-top">
                      <td className="py-2.5 pr-4 whitespace-nowrap text-muted">{dateTime(r.createdAt)}</td>
                      <td className="py-2.5 pr-4">
                        {/* Senza utente l'ha fatto il sistema: uno script, un monitor. */}
                        {r.user ? <span className="text-ink">{r.user.fullName ?? r.user.email}</span> : <span className="text-faint">sistema</span>}
                      </td>
                      <td className="py-2.5 pr-4 text-ink">{r.action}</td>
                      <td className="py-2.5 pr-4 text-muted">
                        {r.entityType}
                        {r.entityId && <span className="machine-id ml-1 text-xs">{r.entityId}</span>}
                      </td>
                      <td className="py-2.5">
                        <Payload value={r.payload} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager total={q.data.total} limit={q.data.limit} offset={offset} onOffset={setOffset} />
          </>
        )}
      </div>
    </>
  );
}

/**
 * Il payload è JSON libero e cambia con l'azione: si mostra com'è, aperto solo
 * se qualcuno lo chiede. Inventare una forma per ogni azione vorrebbe dire
 * nascondere il campo che quel giorno serviva.
 */
function Payload({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-faint">—</span>;
  const text = JSON.stringify(value, null, 2);
  const corto = text.length <= 80;
  if (corto) return <code className="text-xs text-muted">{text}</code>;
  return (
    <details>
      <summary className="cursor-pointer text-xs text-muted">{text.slice(0, 60)}…</summary>
      <pre className="mt-1 max-w-[40rem] overflow-x-auto rounded bg-raised/60 p-2 text-xs text-muted">{text}</pre>
    </details>
  );
}
